import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import relatedItemsApi from 'common/src/modules/relatedItems/relatedItems.api';

const setFeedToolSchema = z.object({
  feedId: z.number().describe('The feed ID to set for the product card'),
});

export default function setFeedTool() {
  async function execute({
    input: {feedId},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<typeof setFeedToolSchema, ProductCardScratchpadState>): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    if (!validatedScratchpad.card.availableFeeds?.length) {
      return toolError(
        'No feeds available for this card. First set the strategy, feeds are available when the strategy is set.',
      );
    }

    if (!validatedScratchpad.card.availableFeeds?.some((f) => f.id === feedId)) {
      return toolError(
        `Feed with ID ${feedId} is not available for this card. Available feeds: ${validatedScratchpad.card.availableFeeds?.map((f) => `${f.id}: ${f.name}`).join(', ')}`,
      );
    }

    validatedScratchpad.card.feedId = feedId;
    validatedScratchpad.card.feed = validatedScratchpad.card.availableFeeds?.find(
      (f) => f.id === feedId,
    );
    validatedScratchpad.card.topItemLogicId = 0;
    validatedScratchpad.card.categoryTopItemsLogicId = 0;
    validatedScratchpad.card.retargetingProductSetId = 0;
    validatedScratchpad.card.productIds = [];

    const relatedItems = await relatedItemsApi.getRelatedItemsLogicsForFeed({feedId});
    validatedScratchpad.card.relatedItemsLogics = relatedItems;

    return toolResult(
      `Feed set to: ${feedId}, you can continue setting the strategy related items`,
    );
  }

  return tool({
    name: V2CardAgentToolName.SetFeed,
    displayName: 'Set feed',
    description: 'Set the feed for the product card',
    execute,
    inputSchema: setFeedToolSchema,
  });
}
